import { useState, useEffect } from 'react'
import { Navigate, useLocation } from 'react-router-dom'
import { useAuth } from '../../hooks/useAuth'
import { getUserYachts } from '../../services/firestore'
import HelmIcon from './HelmIcon'

export default function ProtectedRoute({ children, requiresYacht = false }) {
  const { isAuthenticated, loading, profile } = useAuth()
  const location = useLocation()
  const [checkingYachts, setCheckingYachts] = useState(requiresYacht)
  const [hasYachts, setHasYachts] = useState(null)

  // Debug logging
  console.log('[PROTECTED] Route check:', {
    path: location.pathname,
    isAuthenticated,
    loading,
    profileId: profile?.id,
    requiresYacht,
    checkingYachts,
    hasYachts
  })

  useEffect(() => {
    const checkYachts = async () => {
      if (requiresYacht && profile?.id) {
        console.log('ProtectedRoute: Checking yachts for user:', profile.id)

        // If user has completed onboarding, skip the yacht check
        if (profile.onboarding_completed) {
          console.log('ProtectedRoute: Onboarding already completed, skipping yacht check')
          setHasYachts(true)
          setCheckingYachts(false)
          return
        }

        try {
          const yachts = await getUserYachts(profile.id)
          console.log('ProtectedRoute: Yachts returned:', yachts?.length || 0)
          setHasYachts((yachts || []).length > 0)
        } catch (err) {
          console.error('ProtectedRoute: Error checking yachts:', err)
          // On error, don't block — let them through
          setHasYachts(true)
        }
        setCheckingYachts(false)
      }
    }

    if (requiresYacht && profile?.id) {
      checkYachts()
    } else if (!requiresYacht) {
      setCheckingYachts(false)
    }
  }, [profile?.id, requiresYacht])

  // Show loading while checking auth
  if (loading || checkingYachts) {
    return (
      <div className="min-h-screen bg-navy-900 flex items-center justify-center">
        <div className="text-center">
          <div className="relative">
            <div className="w-20 h-20 bg-white/10 rounded-2xl flex items-center justify-center mx-auto animate-pulse">
              <HelmIcon className="w-10 h-10 text-white" />
            </div>
            <div className="absolute inset-0 rounded-2xl border-2 border-gold-500 animate-ping opacity-30" />
          </div>
          <p className="mt-6 text-lg font-medium text-white">Loading YachtState</p>
          <div className="mt-4 flex items-center justify-center gap-1">
            <div className="w-2 h-2 bg-gold-500 rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
            <div className="w-2 h-2 bg-gold-500 rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
            <div className="w-2 h-2 bg-gold-500 rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
          </div>
        </div>
      </div>
    )
  }

  // Not authenticated - redirect to login
  if (!isAuthenticated) {
    return <Navigate to="/login" state={{ from: location }} replace />
  }

  // Requires yacht but user has none - redirect to onboarding
  if (requiresYacht && hasYachts === false) {
    return <Navigate to="/onboarding" replace />
  }

  return children
}
